/**
 * @description
 * Service d'orchestration de la facturation FSE silencieuse (Phase 2.2)
 * pour les contrats de location (doc_type = LOCATION).
 *
 * Séquence d'exécution pour chaque contrat :
 *  1. Récupération du modèle FSE (fse_order d'état MODELE_LOCATION) via GET /hc/fse/{template_fse_id}
 *  2. Clonage profond de `factureFR` et adaptation de la date de facturation
 *  3. Création d'un brouillon FSE enfant (EVENTTYP_INSERT → handler process_fse_order_insert)
 *  4. Sécurisation silencieuse Area (topSilencieux=true, topSimulation=false, SESAM_SANS_VITALE)
 *  5. En cas d'échec Area : Rollback automatique par suppression du brouillon enfant (EVENTTYP_DELETE)
 *  6. Enregistrement de la FSE sécurisée (EVENTTYP_FSE_UPDATEORDERDATA → etat_facture='SECURISÉE')
 *  7. Retour du mapping { [order_id]: new_fse_id } pour l'appel backend RENTAL_INVOICING
 *
 * Dépendances :
 * - MercalinkFselibService (facturation Area)
 * - LoginService (area_baseurl, configarea, loggeduser)
 * - HttpClient (appels backend)
 * - AlertService (rapport d'erreurs utilisateur)
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, concat, throwError } from 'rxjs';
import { switchMap, map, catchError, reduce } from 'rxjs/operators';
import moment from 'moment';
import { MercalinkFselibService, MercalinkAPIService, dao, tofseformat, ProcessingResult, ConfigArea } from 'mercalink-fselib';
import { environment } from '@app/../environments/environment';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { Sales_order } from '@app/shared/_models/sales';
import { jsonheader, ObjectType, EventType } from '@app/shared/constants';

import { PreparedRentalContractInvoice } from '@app/sales/quotations/quotations.service';
import { FseService } from '@app/fse/fse.service';

/** Mapping order_id → new_fse_id retourné pour le backend RENTAL_INVOICING */
export type FseOrdersMapping = Record<number, number>;

/** Résultat de la sécurisation unitaire d'une FSE */
export interface SingleFseRentalResult {
  orderId: number;
  fseId: number;
}

// Jour de Paris « AAAA-MM-JJ » de l'aperçu → AAAAMMJJ de la FSE, sans Date : aucun fuseau ne peut le décaler ; absent ou illisible, la FSE n'est pas créée.
export function fseDay(day: string | null | undefined, what: string): string {
  const m = typeof day === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  if (!m) {
    throw new Error(`${what} « ${day ?? 'absent'} » illisible (AAAA-MM-JJ attendu), FSE non créée`);
  }
  return `${m[1]}${m[2]}${m[3]}`;
}

// Centimes au plus proche, moitié vers le haut, comme le Decimal du serveur qui compare la FSE au passage : 1550 × 0,57 vaut 883,4999… en flottant, 884 attendu.
export function fseCents(x: number): number {
  return Math.round(Number(x.toFixed(6)));
}

@Injectable({
  providedIn: 'root',
})
export class RentalFseService {
  private http = inject(HttpClient);
  private loginService = inject(LoginService);
  private fseService = inject(MercalinkFselibService);
  private apiService = inject(MercalinkAPIService);
  private alertService = inject(AlertService);
  private appFseService = inject(FseService);

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers publics
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Indique si un contrat de location doit passer par la sécurisation FSE Area.
   * Vrai ssi le contrat possède un `fse_id` modèle dans `data`.
   *
   * @param order Contrat de location à tester
   * @returns `true` si le contrat est de type FSE Location
   */
  public isRentalFse(order: Sales_order | { id?: number; data?: any }): boolean {
    return !!order?.data?.['fse_id'];
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Orchestration groupée (traitement séquentiel)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Orchestre la facturation FSE silencieuse pour une liste de contrats de location.
   * Les contrats sont traités **séquentiellement** pour respecter l'accès exclusif au lecteur CPS.
   *
   * @param orders Liste des contrats de location FSE à facturer
   * @returns Observable émettant le mapping `{ [order_id]: new_fse_id }` agrégé
   */
  public processFseRentals$(orders: Sales_order[]): Observable<FseOrdersMapping> {
    const fseOrders = orders.filter(o => this.isRentalFse(o));
    if (fseOrders.length === 0) {
      return of({});
    }

    return concat(...fseOrders.map(order => this.processSingleFseRental$(order).pipe(map(r => ({ [r.orderId]: r.fseId }))))).pipe(reduce((acc: FseOrdersMapping, partial: FseOrdersMapping) => ({ ...acc, ...partial }), {}));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Traitement unitaire d'un contrat
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Exécute la séquence complète de facturation FSE silencieuse pour un seul contrat.
   *
   * @param order Contrat de location FSE (doit vérifier `isRentalFse`)
   * @param preparedInvoice Données de préparation d'éligibilité (dates de période, montants)
   * @returns Observable émettant `{ orderId, fseId }`
   */
  public processSingleFseRental$(order: Sales_order | { id: number; data?: any }, preparedInvoice?: PreparedRentalContractInvoice): Observable<SingleFseRentalResult> {
    const templateFseId: number | undefined = order?.data?.['fse_id'] ?? preparedInvoice?.fse_template_id;
    if (!templateFseId) {
      console.warn(`[RentalFseService] Contrat #${order.id} sans fse_id modèle, ignoré`);
      return of({ orderId: order.id, fseId: 0 });
    }

    const areaBaseurl = this.loginService.area_baseurl;
    const configarea: ConfigArea | undefined = this.loginService.currentstation?.data?.configarea;
    const userId = this.loginService.loggeduser?.id ?? 0;

    if (!areaBaseurl || !configarea) {
      throw new Error(`Station non configurée (area_baseurl ou configarea absent) pour contrat #${order.id}`);
    }

    // Étape 1 — Récupération du modèle FSE template
    return this.getFseTemplate$(templateFseId).pipe(
      switchMap(templateFse => {
        if (!templateFse?.data?.factureFR) {
          throw new Error(`Modèle FSE #${templateFseId} invalide ou sans factureFR`);
        }

        // Étape 2 — Clonage & adaptation du factureFR
        const draftData = this.cloneAndAdaptFactureFR(templateFse, order, preparedInvoice);

        // Étape 3 — Création du brouillon FSE enfant
        return this.insertDraftFse$(draftData, userId).pipe(
          switchMap(insertResp => {
            const newFseId: number = insertResp?.data?.id ?? insertResp?.id;
            if (!newFseId) {
              throw new Error(`Brouillon FSE enfant non créé pour contrat #${order.id} (réponse: ${JSON.stringify(insertResp)})`);
            }
            console.log(`[RentalFseService] Brouillon FSE #${newFseId} créé pour contrat #${order.id}`);

            // Étape 4 — Sécurisation silencieuse Area (sans Vitale, sans UI)
            const silentConfig: ConfigArea = {
              ...configarea,
              topSimulation: 'false',
              topSilencieux: 'false', // TODO Set back to true in production
            };

            return this.fseService
              .facturer(
                areaBaseurl,
                environment.server_url,
                newFseId,
                order.id,
                userId,
                false, // topSimulation
                false, // topAssistance
                silentConfig,
              )
              .pipe(
                switchMap((result: ProcessingResult) => {
                  // Vérification du résultat Area
                  if (result.fatal && result.fatal.length > 0) {
                    throw new Error(`Facturation Area fatale : ${result.fatal.join(', ')}`);
                  }
                  if (result.errors && result.errors.length > 0) {
                    throw new Error(`Facturation Area échouée : ${result.errors.join(', ')}`);
                  }
                  if (result.areaResponse?.HTTPResponse?.codeRetour !== '0') {
                    const code = result.areaResponse?.HTTPResponse?.codeRetour ?? '?';
                    const lib = result.areaResponse?.HTTPResponse?.libRetour ?? '';
                    throw new Error(`Area codeRetour=${code} : ${lib}`);
                  }

                  console.log(`[RentalFseService] FSE #${newFseId} sécurisée (Area codeRetour=0)`);

                  // Étape 5 — Enregistrement de la FSE sécurisée côté backend
                  const updateData = {
                    id: newFseId,
                    etat_facture: 'SECURISÉE',
                    state: 'SECURISÉE',
                    processingResult: result,
                  };

                  return this.updateFseOrderData$(newFseId, userId, updateData).pipe(
                    switchMap(() => this.processAttachmentsForFse$(templateFseId, newFseId, order, draftData)),
                    map(() => ({ orderId: order.id, fseId: newFseId })),
                  );
                }),
                catchError(err => {
                  console.warn(`[RentalFseService] Échec sécurisation FSE #${newFseId} pour contrat #${order.id}. Rollback : suppression du brouillon...`, err);
                  return this.deleteDraftFse$(newFseId, order.id, userId).pipe(
                    catchError(delErr => {
                      console.error(`[RentalFseService] Échec de suppression du brouillon FSE #${newFseId}:`, delErr);
                      return of(null);
                    }),
                    switchMap(() => throwError(() => err)),
                  );
                }),
              );
          }),
        );
      }),
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers HTTP privés
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Récupère le modèle FSE depuis le backend.
   *
   * @param fseId Identifiant du modèle FSE (état MODELE_LOCATION)
   * @returns Observable émettant le premier résultat FSEOrder
   */
  private getFseTemplate$(fseId: number): Observable<dao.FSEOrder> {
    return this.http.get<dao.FSEOrder[]>(`${environment.server_url}/hc/fse/${fseId}`).pipe(
      map(results => {
        if (!Array.isArray(results) || results.length === 0) {
          throw new Error(`Modèle FSE #${fseId} introuvable en BD`);
        }
        return results[0];
      }),
    );
  }

  /**
   * Crée un brouillon FSE enfant (état TARIFICATION) via EVENTTYP_INSERT.
   * Le backend process_fse_order_insert affecte un numFacture et retourne l'id.
   *
   * @param data Données du brouillon FSE (factureFR adapté + etat_facture)
   * @param userId Identifiant de l'utilisateur courant
   */
  // TODO Use postFseOrderEvent from fse-lib (apiclient)
  private insertDraftFse$(data: any, userId: number): Observable<any> {
    const event = {
      id: 0,
      typ: EventType.INSERT,
      userid: userId,
      objectid: 0,
      objecttyp: ObjectType.HCORDER,
      data,
    };
    return this.http.post<any>(`${environment.server_url}/event`, event, { headers: jsonheader });
  }

  /**
   * Met à jour les données de sécurisation Area sur la FSE enfant.
   * Passe l'état à SECURISÉE et enregistre le processingResult Area.
   *
   * @param fseId Identifiant de la FSE enfant nouvellement créée
   * @param userId Identifiant de l'utilisateur courant
   * @param data Données de mise à jour (etat_facture + processingResult)
   */
  private updateFseOrderData$(fseId: number, userId: number, data: any): Observable<any> {
    const event = {
      id: 0,
      typ: EventType.FSE_UPDATE_ORDER_DATA,
      userid: userId,
      objectid: fseId,
      objecttyp: ObjectType.HCORDER,
      data,
    };
    return this.http.post<any>(`${environment.server_url}/event`, event, { headers: jsonheader });
  }

  /**
   * Supprime le brouillon FSE enfant (Rollback) en cas d'échec de sécurisation Area.
   * Empêche de laisser des lignes orphelines en état TARIFICATION en BD.
   *
   * @param fseId Identifiant de la FSE enfant à supprimer
   * @param orderId Identifiant de la commande parent (requis par FseOrder côté backend)
   * @param userId Identifiant de l'utilisateur courant
   */
  private deleteDraftFse$(fseId: number, orderId: number, userId: number): Observable<any> {
    const event = {
      id: 0,
      typ: EventType.DELETE,
      userid: userId,
      objectid: fseId,
      objecttyp: ObjectType.HCORDER,
      data: {
        id: fseId,
        id_order: orderId,
        etat_facture: 'TARIFICATION',
        data: {},
      },
    };
    return this.http.post<any>(`${environment.server_url}/event`, event, { headers: jsonheader });
  }

  /**
   * Transmet les PJ (ordonnance) à Area lors de la création d'une FSE enfant.
   * Si la FSE est la première (les docs n'ont pas d'idpj), on envoie le document complet.
   * Sinon, on envoie une REF_EXTERNE.
   */
  private processAttachmentsForFse$(
    templateFseId: number,
    newFseId: number,
    order: Sales_order | { id: number; data?: any },
    draftData: any, // The adapted draft data containing factureFR
  ): Observable<any> {
    const areaBaseurl = this.loginService.area_baseurl;
    // Numéro d'identification du facturateur (PS) : lu en priorité dans la facture FSE clonée,
    // avec repli sur le profil FSE du praticien connecté si absent.
    const numIdentFact = draftData.factureFR?.PS?.numIdentFact || this.loginService.loggeduser?.data?.FSE?.PS?.numIdentFact || '';
    const numFacture = newFseId.toString().padStart(9, '0');
    let dateFacture = draftData.factureFR?.dateFacture ?? '';
    if (dateFacture) {
      dateFacture = tofseformat(dateFacture);
    }

    if (!areaBaseurl) {
      console.warn('[processAttachmentsForFse$] area_baseurl manquant, pièces jointes non envoyées');
      return of(null);
    }
    // Get fse_doc[] from rental template
    return this.apiService.getDocsByFseId$(environment.server_url, templateFseId).pipe(
      switchMap((docs: dao.FseDoc[]) => {
        if (!docs || docs.length === 0) {
          return of(null);
        }

        const attachRequests = docs.map(doc => {
          if (!doc.idpj) {
            // Étape 2: Première FSE enfant, document non tranmis, pas d'idpj.
            return this.apiService.getDocById$(environment.server_url, doc.id).pipe(
              switchMap((fullDocs: dao.FseDoc[]) => {
                const fullDoc = fullDocs[0];
                if (!fullDoc) return of(null);

                let dateConst = draftData.factureFR?.prescription?.datePresc ?? '';
                if (dateConst) {
                  dateConst = tofseformat(dateConst) + '000000';
                }

                const pj: dao.PJ = {
                  nature: fullDoc.nature,
                  idFactResponsable: numIdentFact,
                  dateConstitution: dateConst,
                  type: fullDoc.typepj,
                  idPJ: undefined,
                  contenu: (fullDoc as any).content_base64,
                };

                /* Gemini bullshit
                if (pj.type !== 'PDF') {
                  delete pj['contenu'];
                }
                if (pj.type === 'PDF' || pj.type === 'PAPIER') {
                  delete pj['idPJ'];
                }
                */
                delete pj['idPJ'];

                return this.fseService.ajouterPJ$(areaBaseurl, numIdentFact, numFacture, dateFacture, pj).pipe(
                  catchError(err => {
                    console.error('[processAttachmentsForFse$] Error ajouterPJ$ full doc', err);
                    return throwError(() => new Error(`Erreur réseau/interne Area : ${err.message || err}`));
                  }),
                  switchMap((res: dao.AjouterPJHTTPResponse | null) => {
                    if (res?.HTTPResponse?.codeRetour === '0' && res.HTTPResponse.PJ?.idPJ) {
                      doc.idpj = res.HTTPResponse.PJ.idPJ;
                      // Update idpj in fse_doc table for template. Marking the doc as transmitted
                      // so that next FSE childs will use REF_EXTERNE type.
                      return this.apiService.postDocUpdateIdPJEvent$(environment.server_url, doc).pipe(
                        catchError(err => {
                          console.error('[processAttachmentsForFse$] Error postDocUpdateIdPJEvent$', err);
                          return throwError(() => new Error(`Erreur lors de la sauvegarde du document modèle : ${err.message || err}`));
                        }),
                        switchMap(() => this.duplicateDocumentForChild$(newFseId, numIdentFact, fullDoc, doc.idpj!)),
                      );
                    }
                    return throwError(() => new Error(`Erreur Area ajouterPJ : ${res?.HTTPResponse?.codeRetour} - ${res?.HTTPResponse?.libRetour}`));
                  }),
                );
              }),
            );
          } else {
            // Étape 3: FSE suivantes, le document a déjà un idpj.
            let dateConst = draftData.factureFR?.prescription?.datePresc ?? '';
            if (dateConst) {
              dateConst = tofseformat(dateConst) + '000000';
            }

            const pj: dao.PJ = {
              nature: doc.nature,
              idFactResponsable: numIdentFact,
              dateConstitution: dateConst,
              type: 'REF_EXTERNE',
              idPJ: doc.idpj,
              contenu: undefined,
            };

            delete pj['contenu'];

            return this.fseService.ajouterPJ$(areaBaseurl, numIdentFact, numFacture, dateFacture, pj).pipe(
              catchError(err => {
                console.error('[processAttachmentsForFse$] Error ajouterPJ$ REF_EXTERNE', err);
                return throwError(() => new Error(`Erreur réseau/interne Area (REF_EXTERNE) : ${err.message || err}`));
              }),
              switchMap((res: dao.AjouterPJHTTPResponse | null) => {
                if (res?.HTTPResponse?.codeRetour === '0') {
                  return this.createExternalRefDocForChild$(newFseId, numIdentFact, doc.nature, doc.idpj!);
                }
                return throwError(() => new Error(`Erreur Area ajouterPJ (REF_EXTERNE) : ${res?.HTTPResponse?.codeRetour} - ${res?.HTTPResponse?.libRetour}`));
              }),
            );
          }
        });

        // Execute all sequentially
        return concat(...attachRequests).pipe(reduce((acc, val) => acc, null));
      }),
    );
  }

  private duplicateDocumentForChild$(fseId: number, numIdentFact: string, fullDoc: dao.FseDoc, idpj: string): Observable<any> {
    const formData = new FormData();
    let fileBlob: Blob;
    if ((fullDoc as any).content_base64) {
      try {
        const byteCharacters = atob((fullDoc as any).content_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const mimeType = fullDoc.typepj === 'PDF' ? 'application/pdf' : 'text/plain';
        fileBlob = new Blob([byteArray], { type: mimeType });
      } catch (e) {
        console.error('Failed to decode base64', e);
        fileBlob = new Blob([''], { type: 'text/plain' });
      }
    } else {
      fileBlob = new Blob([''], { type: 'text/plain' });
    }

    const fileName = fullDoc.file_name || `Document_${idpj}`;
    const file = new File([fileBlob], fileName, { type: fileBlob.type });
    formData.append('file', file);

    const jsonPayload: any = {
      name: fileName,
      id_fse: fseId,
      numIdentFact: numIdentFact,
      typepj: fullDoc.typepj,
      nature: fullDoc.nature,
      idPJ: idpj,
    };
    const jsonBlob = new Blob([JSON.stringify(jsonPayload)], { type: 'application/json' });
    formData.append('json', jsonBlob);

    return this.apiService.postAttach$(environment.server_url, formData).pipe(
      catchError(err => {
        console.error('[duplicateDocumentForChild$] Error postAttach$', err);
        return of(null);
      }),
    );
  }

  private createExternalRefDocForChild$(fseId: number, numIdentFact: string, nature: string, idpj: string): Observable<any> {
    const formData = new FormData();
    const emptyFileBlob = new Blob([''], { type: 'text/plain' });
    const dummyFile = new File([emptyFileBlob], `Réf. Ext. ${idpj}`, { type: 'text/plain' });
    formData.append('file', dummyFile);

    const jsonPayload: any = {
      name: `Réf. Ext. ${idpj}`,
      id_fse: fseId,
      numIdentFact: numIdentFact,
      typepj: 'REF_EXTERNE',
      nature: nature,
      idPJ: idpj,
    };
    const jsonBlob = new Blob([JSON.stringify(jsonPayload)], { type: 'application/json' });
    formData.append('json', jsonBlob);

    return this.apiService.postAttach$(environment.server_url, formData).pipe(
      catchError(err => {
        console.error('[createExternalRefDocForChild$] Error postAttach$', err);
        return of(null);
      }),
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Adaptation du factureFR modèle → brouillon période courante
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Clone en profondeur le `factureFR` du modèle FSE et adapte les champs temporels
   * à la période de facturation courante.
   *
   * Adaptations :
   * - `dateFacture` → date du jour au format FR JJ/MM/AAAA
   * - Injection des dates de début/fin réelles sur les prestations LPP (`dateExec`, `dateDebut`, `dateFin`)
   * - `etat_facture` → `'TARIFICATION'` (brouillon avant sécurisation)
   *
   * @param templateFse Modèle FSE source (état MODELE_LOCATION)
   * @param order Contrat de location parent
   * @param preparedInvoice Données de préparation d'éligibilité (dates réelles de période)
   * @returns Objet de données prêt pour insertDraftFse$
   */
  private cloneAndAdaptFactureFR(templateFse: dao.FSEOrder, order: Sales_order | { id: number; data?: any }, preparedInvoice?: PreparedRentalContractInvoice): any {
    // Clonage profond
    const clonedFactureFR = JSON.parse(JSON.stringify(templateFse.data.factureFR));

    // Date du jour au format FR JJ/MM/AAAA et STS AAAAMMJJ
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    const stsToday = `${yyyy}${mm}${dd}`;
    clonedFactureFR.dateFacture = `${dd}/${mm}/${yyyy}`;

    let stsStart: string | null = null;

    // Jours de Paris de l'aperçu (BT-134), jamais un instant relu dans le fuseau du poste ; dateRefAMC alimente dateExec dans mercalink-fselib.
    // start du contrat = premier jour du passage (avant le 27/10/2026 : first_period_first_day).
    const periodCount = preparedInvoice?.periods?.length ?? 0;
    if (periodCount > 0) {
      stsStart = fseDay(preparedInvoice?.start, `dossier #${order.id} : start`);
      clonedFactureFR.dateRefAMC = `${stsStart.slice(6, 8)}/${stsStart.slice(4, 6)}/${stsStart.slice(0, 4)}`;
    } else {
      clonedFactureFR.dateRefAMC = clonedFactureFR.dateFacture;
    }

    // Règle 1.8 & Non-répétition des prestations d'accessoires / vente :
    // On détermine la composition exacte de la facture à émettre (location, accessoires, ou les deux).
    const hasRental = preparedInvoice ? (preparedInvoice.has_rental_lines ?? (preparedInvoice.periods?.length ?? 0) > 0) : true;
    const hasItems = preparedInvoice ? (preparedInvoice.has_item_lines ?? (preparedInvoice.lines_count ?? 0) > (preparedInvoice.periods?.length ?? 0)) : false;

    if (!clonedFactureFR.prestationFR) {
      throw new Error(`dossier #${order.id} : le modèle FSE #${templateFse.id} n'a aucune prestation, FSE non créée`);
    }
    if (clonedFactureFR.prestationFR) {
      const allPrestations = Array.isArray(clonedFactureFR.prestationFR) ? clonedFactureFR.prestationFR : [clonedFactureFR.prestationFR];

      const finalPrestations: any[] = [];
      const stsToday = moment().format('YYYYMMDD');

      if (hasItems) {
        for (const presta of allPrestations) {
          if (presta && presta.LPP?.typePrestaLPP !== 'L') {
            const execDate = stsStart ?? stsToday;
            presta.dateExec = execDate;
            if (presta.LPP) {
              presta.LPP.dateDebut = execDate;
              presta.LPP.dateFin = execDate;
            }
            finalPrestations.push(presta);
          }
        }
      }

      if (hasRental && preparedInvoice?.periods) {
        for (const period of preparedInvoice.periods) {
          const where = `dossier #${order.id}, ligne ${period.origin_order_l_id}, ${period.label}`;
          // Code du palier facturé, jamais celui du modèle : sans lui la FACTURE_FSE et la FSE ne diraient pas la même chose.
          const lpp = (period.fse_lpp ?? '').trim();
          if (!lpp) {
            throw new Error(`${where} : période sans code LPP (palier sans code), FSE non créée`);
          }
          // Find the corresponding template prestation
          let modelPresta = allPrestations.find((p: any) => p?.LPP?.typePrestaLPP === 'L' && p.LPP.codeLPP === period.base_fse_lpp);
          if (!modelPresta) {
            modelPresta = allPrestations.find((p: any) => p?.LPP?.typePrestaLPP === 'L');
          }
          if (!modelPresta?.LPP) {
            throw new Error(`${where} : le modèle FSE #${templateFse.id} n'a aucune prestation de location (typePrestaLPP L), FSE non créée`);
          }

          const presta = JSON.parse(JSON.stringify(modelPresta));
          // Période du groupe : start = premier jour (dateDebut, dateExec), end = dernier jour, inclus (dateFin) ; avant : period_first_day / period_last_day.
          const startDt = fseDay(period.start, `${where} : start`);
          presta.dateExec = startDt;
          // Quantité = qty de la ligne de facture (fraction au prorata, 2 décimales), jamais le nombre de rangs couverts.
          const qty = Number(period.qty);
          presta.quantite = String(qty);

          presta.LPP.codeLPP = lpp;
          presta.LPP.dateDebut = startDt;
          // Fin incluse (avenant 21) : end, dernier jour couvert, le même que BT-135.
          presta.LPP.dateFin = fseDay(period.end, `${where} : end`);
          presta.LPP.nbUnites = presta.quantite;

          // tarifLPP expects price in cents (TTC)
          const tarifCents = fseCents(period.uprice_taxi * 100);
          presta.LPP.tarifLPP = tarifCents.toString();
          presta.LPP.prixUnitairePublicLPP = tarifCents.toString(); // Defaulting to same if unknown

          const mtTotal = fseCents(tarifCents * qty);
          presta.LPP.mtTotalLPP = mtTotal.toString();
          presta.LPP.mtTotalTTC = mtTotal.toString();
          presta.prixUnit = presta.LPP.mtTotalLPP;

          delete presta.montantRemboursementAMO;
          delete presta.montantRemboursementAMC;
          finalPrestations.push(presta);
        }
      }

      // Rien à facturer d'après l'aperçu : les prestations du modèle partiraient à la caisse avec ses dates et ses codes.
      if (finalPrestations.length === 0) {
        throw new Error(`dossier #${order.id} : aucune prestation du modèle FSE #${templateFse.id} ne correspond à l'aperçu (ni location ni vente), FSE non créée`);
      }

      if (finalPrestations.length > 0) {
        finalPrestations.sort((a, b) => {
          const dateA = a.dateExec || a.LPP?.dateDebut || '00000000';
          const dateB = b.dateExec || b.LPP?.dateDebut || '00000000';
          return dateA.localeCompare(dateB);
        });
      }

      clonedFactureFR.prestationFR = finalPrestations;
    }

    return {
      factureFR: clonedFactureFR,
      etat_facture: 'TARIFICATION',
      state: 'TARIFICATION',
      order_id: order.id,
    };
  }
  /**
   * Rollback des FSE Area générées. Appelé quand RENTAL_INVOICING échoue (ex: Z de caisse en retard).
   * Parcourt séquentiellement les FSE, récupère leurs documents locaux, appelle Area pour les supprimer,
   * supprime la FSE dans Area, puis déclenche la suppression de l'entité fse_order en base locale.
   *
   * @param fseIds Liste des identifiants des FSE à supprimer
   */
  public rollbackAreaFses$(fseIds: number[]): Observable<any> {
    if (!fseIds || fseIds.length === 0) return of(null);
    const areaBaseurl = this.loginService.area_baseurl;
    // Sans Area, rien n'est annulé : le dire plutôt que laisser croire l'annulation faite.
    if (!areaBaseurl) return throwError(() => new Error('area_baseurl absent, aucune FSE annulée'));

    return concat(
      ...fseIds.map(fseId => {
        let fseOrder: dao.FSEOrder | null = null;
        let numIdentFact = '';
        let dateFacture = '';

        return this.http.get<dao.FSEOrder[]>(`${environment.server_url}/hc/fse/${fseId}`).pipe(
          switchMap(orders => {
            if (!orders || orders.length === 0) return of(null);
            fseOrder = orders[0];
            const factFR = fseOrder?.data?.factureFR;
            if (!factFR) return of(null);

            dateFacture = tofseformat(factFR.dateFacture);
            numIdentFact = factFR.PS?.numIdentFact || this.loginService.loggeduser?.data?.FSE?.PS?.numIdentFact || '';

            return this.apiService.getDocsByFseId$(environment.server_url, fseId).pipe(
              switchMap((docs: dao.FseDoc[]) => {
                if (!docs || docs.length === 0) return of(null);

                return concat(
                  ...docs.map((doc: dao.FseDoc) => {
                    let deleteArea$: Observable<any> = of(null);
                    if (doc.idpj) {
                      deleteArea$ = this.fseService.supprimerPJ$(areaBaseurl, numIdentFact, factFR.numFacture, dateFacture, doc.idpj).pipe(
                        catchError(err => {
                          console.warn('[Rollback] Area supprimerPJ$ error, ignoring', err);
                          return of(null);
                        }),
                      );
                    }
                    return deleteArea$.pipe(
                      switchMap(() =>
                        this.apiService.postDocDeleteEvent$(environment.server_url, doc).pipe(
                          catchError(e => {
                            console.warn('[Rollback] DB postDocDeleteEvent$ error, ignoring', e);
                            return of(null);
                          }),
                        ),
                      ),
                    );
                  }),
                ).pipe(reduce(() => null, null));
              }),
            );
          }),
          switchMap(() => {
            if (fseOrder && fseOrder.data?.factureFR?.numFacture) {
              return this.fseService.supprimerFSE$(areaBaseurl, numIdentFact, fseOrder.data.factureFR.numFacture, dateFacture).pipe(
                catchError(err => {
                  console.warn('[Rollback] Area supprimerFSE$ error, ignoring', err);
                  return of(null);
                }),
              );
            }
            return of(null);
          }),
          switchMap(() => {
            if (fseOrder) {
              return this.appFseService.postFSEDeleteEvent$(environment.server_url, fseOrder).pipe(
                catchError(e => {
                  console.warn('[Rollback] DB postFSEDeleteEvent$ error, ignoring', e);
                  return of(null);
                }),
              );
            }
            return of(null);
          }),
        );
      }),
    ).pipe(reduce(() => null, null));
  }
}
